import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Bulk create work entries
export const bulkCreateWorkEntries = createAsyncThunk(
  "workEntry/bulkCreate",
  async ({ date, entries }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        "/work-entries/bulk",
        { date, entries }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") ||
        "Failed to create work entries"
      );
    }
  }
);

// Get work entries report
export const getWorkEntriesReport = createAsyncThunk(
  "workEntry/getReport",
  async (filters, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/work-entries/report", {
        params: filters,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch report"
      );
    }
  }
);

// Download report
export const downloadWorkEntriesReport = createAsyncThunk(
  "workEntry/downloadReport",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await api.get(
        "/work-entries/report/download",
        {
          params: filters,
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download report"
      );
    }
  }
);

const workEntrySlice = createSlice({
  name: "workEntry",
  initialState: {
    report: null,
    loading: false,
    error: null,
    success: false,
    message: null,
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    clearReport: (state) => {
      state.report = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Bulk create
      .addCase(bulkCreateWorkEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(bulkCreateWorkEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(bulkCreateWorkEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get report
      .addCase(getWorkEntriesReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWorkEntriesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload.data;
      })
      .addCase(getWorkEntriesReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Download report
      .addCase(downloadWorkEntriesReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadWorkEntriesReport.fulfilled, (state, action) => {
        state.loading = false;
        const blob = new Blob([action.payload], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `work-report-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .addCase(downloadWorkEntriesReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, clearMessage, resetSuccess, clearReport } =
  workEntrySlice.actions;
export default workEntrySlice.reducer;
